import { getSchoolTracking } from "./outreachTrackingFirestore";
import {
  MAX_SCHOOLS_PER_USER,
  normalizeEmail,
  getOutreachUserName,
} from "./outreachMvpCredentials";

function emptyMember(email, name, isAdmin = false) {
  return {
    email,
    name,
    isAdmin,
    claimed: 0,
    called: 0,
    answered: 0,
    messaged: 0,
    quizFull: 0,
    quizPartial: 0,
    quizNone: 0,
    quizPending: 0,
    quizConfirmedOnPortfolio: 0,
    newQuizTeams: 0,
    ictRegistered: 0,
    totalRegs: 0,
    quizConfirmationsDone: 0,
    schools: [],
    performanceScore: 0,
    callRate: 0,
    messageRate: 0,
    quizCompleteRate: 0,
  };
}

/**
 * @param {Record<string, object>} schoolState
 * @param {object[]} schools — enriched outreach rows (quizTeams, quizPending, …)
 * @param {{ email: string, name: string, isAdmin?: boolean }[]} members
 */
export function computeOutreachMemberStats(schoolState, schools, members) {
  const schoolsByKey = new Map(schools.map((s) => [s.key, s]));
  const statsByEmail = new Map();

  for (const m of members) {
    const email = normalizeEmail(m.email);
    statsByEmail.set(email, emptyMember(email, m.name, Boolean(m.isAdmin)));
  }

  for (const schoolKey of Object.keys(schoolState || {})) {
    const tracking = getSchoolTracking(schoolState, schoolKey);
    const owner = tracking.lockedByEmail;
    if (!owner) continue;

    if (!statsByEmail.has(owner)) {
      statsByEmail.set(
        owner,
        emptyMember(owner, getOutreachUserName(owner), false)
      );
    }

    const school = schoolsByKey.get(schoolKey);
    const member = statsByEmail.get(owner);
    member.claimed++;

    const quizTeams = school?.quizTeams ?? 0;
    const quizPending = school?.quizPending ?? false;
    const quizCurrent = school?.quizCurrentTeams ?? 0;
    const hasIct = school?.hasIct ?? false;
    const regCount = school?.count ?? 0;

    if (tracking.callStatus && tracking.callStatus !== "not_called") {
      member.called++;
    }
    if (tracking.callStatus === "answered") member.answered++;
    if (tracking.messageSent) member.messaged++;

    if (quizTeams >= 2) member.quizFull++;
    else if (quizTeams === 1) member.quizPartial++;
    else member.quizNone++;

    if (quizPending) member.quizPending++;
    else if ((school?.quizStaleTeams ?? 0) > 0) {
      member.quizConfirmedOnPortfolio++;
    }

    member.newQuizTeams += quizCurrent;
    if (hasIct) member.ictRegistered++;
    member.totalRegs += regCount;

    member.schools.push({
      key: schoolKey,
      displayName: school?.displayName ?? schoolKey,
      quizTeams,
      quizPending,
      quizCurrentTeams: quizCurrent,
      quizStaleTeams: school?.quizStaleTeams ?? 0,
      hasIct,
      regCount,
      callStatus: tracking.callStatus,
      messageSent: tracking.messageSent,
      lockedAt: tracking.lockedAt,
      contactPhone: tracking.contactPhone,
      contactRole: tracking.contactRole,
      notes: tracking.notes,
    });
  }

  for (const raw of Object.values(schoolState || {})) {
    const confirmedBy = normalizeEmail(raw?.quizConfirmedBy);
    if (!confirmedBy || !statsByEmail.has(confirmedBy)) continue;
    statsByEmail.get(confirmedBy).quizConfirmationsDone++;
  }

  const results = [];
  for (const member of statsByEmail.values()) {
    const { claimed } = member;
    member.callRate = claimed ? Math.round((member.called / claimed) * 100) : 0;
    member.messageRate = claimed
      ? Math.round((member.messaged / claimed) * 100)
      : 0;
    member.quizCompleteRate = claimed
      ? Math.round((member.quizFull / claimed) * 100)
      : 0;

    member.performanceScore = computePerformanceScore(member);
    member.schools.sort((a, b) => b.regCount - a.regCount || a.displayName.localeCompare(b.displayName));
    results.push(member);
  }

  results.sort((a, b) => {
    if (b.performanceScore !== a.performanceScore) {
      return b.performanceScore - a.performanceScore;
    }
    if (b.claimed !== a.claimed) return b.claimed - a.claimed;
    return a.name.localeCompare(b.name);
  });

  return results;
}

function computePerformanceScore(member) {
  if (member.claimed === 0) return 0;

  const claimUtil = (member.claimed / MAX_SCHOOLS_PER_USER) * 15;
  const callPts = (member.called / member.claimed) * 25;
  const msgPts = (member.messaged / member.claimed) * 20;
  const quizPts = (member.quizFull / member.claimed) * 25;
  const confirmPts =
    member.claimed > 0
      ? ((member.claimed - member.quizPending) / member.claimed) * 10
      : 0;
  const activityPts = Math.min(member.quizConfirmationsDone * 2, 5);

  return Math.round(
    Math.min(100, claimUtil + callPts + msgPts + quizPts + confirmPts + activityPts)
  );
}

export function computeTeamTotals(memberStats) {
  const active = memberStats.filter((m) => m.claimed > 0);
  return {
    memberCount: memberStats.length,
    activeMembers: active.length,
    totalClaimed: memberStats.reduce((s, m) => s + m.claimed, 0),
    totalCalled: memberStats.reduce((s, m) => s + m.called, 0),
    totalMessaged: memberStats.reduce((s, m) => s + m.messaged, 0),
    totalQuizFull: memberStats.reduce((s, m) => s + m.quizFull, 0),
    avgPerformance: active.length
      ? Math.round(
          active.reduce((s, m) => s + m.performanceScore, 0) / active.length
        )
      : 0,
  };
}

export function performanceTier(score) {
  if (score >= 75) return { label: "Excellent", className: "perf-excellent" };
  if (score >= 50) return { label: "Good", className: "perf-good" };
  if (score >= 25) return { label: "Building", className: "perf-building" };
  if (score > 0) return { label: "Starting", className: "perf-starting" };
  return { label: "Inactive", className: "perf-inactive" };
}
